import React, { useState, useEffect } from 'react'
import { Check, ListFilter, Calendar, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { DateRangePicker } from '@/components/date-picker/date-range-picker'
import { api } from '../../services/api'

export interface StudentFilters {
  collegeId: string[]
  courseGroupId: string[]
  batchYear: string[]
  enrollmentStatus: string[]
  enrollmentStartDate: string
  enrollmentEndDate: string
}

interface DateRange {
  from?: Date
  to?: Date
}

interface StudentFilterDrawerProps {
  isOpen: boolean
  onClose: () => void
  onApplyFilters: (filters: StudentFilters) => void
  currentFilters: StudentFilters
}

interface College {
  id: string
  name: string
  code: string
}

interface CourseGroup {
  id: string
  name: string
  code: string
}

interface FilterInfo {
  colleges?: College[]
  courseGroups: CourseGroup[]
  batchYears: string[]
  enrollmentStatuses: string[]
}

const StudentFilterDrawer: React.FC<StudentFilterDrawerProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  currentFilters
}) => {
  const [tempFilters, setTempFilters] = useState<StudentFilters>(currentFilters)
  const [filterInfo, setFilterInfo] = useState<FilterInfo>({
    colleges: [],
    courseGroups: [],
    batchYears: [],
    enrollmentStatuses: []
  })
  const [isLoadingInfo, setIsLoadingInfo] = useState(false)
  const [dateRange, setDateRange] = useState<DateRange>({
    from: currentFilters.enrollmentStartDate ? new Date(currentFilters.enrollmentStartDate) : undefined,
    to: currentFilters.enrollmentEndDate ? new Date(currentFilters.enrollmentEndDate) : undefined
  })

  // Update temp filters when current filters change
  useEffect(() => {
    setTempFilters(currentFilters)
    setDateRange({
      from: currentFilters.enrollmentStartDate ? new Date(currentFilters.enrollmentStartDate) : undefined,
      to: currentFilters.enrollmentEndDate ? new Date(currentFilters.enrollmentEndDate) : undefined
    })
  }, [currentFilters])

  // Fetch filter info when component mounts
  useEffect(() => {
    if (isOpen) {
      fetchFilterInfo()
    }
  }, [isOpen])

  const fetchFilterInfo = async () => {
    setIsLoadingInfo(true)
    try {
      // Fetch students filter info (includes colleges for SYSTEM_ADMIN)
      const studentsInfoResponse = await api.get('/students/info')
      const studentsInfo = studentsInfoResponse.data.data
      
      setFilterInfo({
        colleges: studentsInfo.colleges || [], // Colleges included if user is SYSTEM_ADMIN
        courseGroups: studentsInfo.courseGroups || [],
        batchYears: studentsInfo.batchYears || [],
        enrollmentStatuses: studentsInfo.enrollmentStatuses || []
      })
    } catch (error) {
      console.error('Failed to fetch filter info:', error)
    } finally {
      setIsLoadingInfo(false)
    }
  }

  const enrollmentStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'graduated', label: 'Graduated' },
    { value: 'dropped_out', label: 'Dropped Out' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'transferred', label: 'Transferred' },
    { value: 'inactive', label: 'Inactive' }
  ]

  const handleCollegeChange = (collegeId: string, checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      collegeId: checked 
        ? [...prev.collegeId, collegeId]
        : prev.collegeId.filter(id => id !== collegeId)
    }))
  }

  const handleCourseGroupChange = (courseGroupId: string, checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      courseGroupId: checked 
        ? [...prev.courseGroupId, courseGroupId]
        : prev.courseGroupId.filter(id => id !== courseGroupId)
    }))
  }

  const handleBatchYearChange = (batchYear: string, checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      batchYear: checked 
        ? [...prev.batchYear, batchYear]
        : prev.batchYear.filter(year => year !== batchYear)
    }))
  }

  const handleEnrollmentStatusChange = (status: string, checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      enrollmentStatus: checked 
        ? [...prev.enrollmentStatus, status]
        : prev.enrollmentStatus.filter(s => s !== status)
    }))
  }

  const handleDateChange = (field: 'enrollmentStartDate' | 'enrollmentEndDate', value: string) => {
    setTempFilters(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range || { from: undefined, to: undefined })
    setTempFilters(prev => ({
      ...prev,
      enrollmentStartDate: range?.from ? range.from.toISOString().split('T')[0] : '',
      enrollmentEndDate: range?.to ? range.to.toISOString().split('T')[0] : ''
    }))
  }

  const handleApply = () => {
    onApplyFilters(tempFilters)
    onClose()
  }

  const handleClearAll = () => {
    const clearedFilters: StudentFilters = {
      collegeId: [],
      courseGroupId: [],
      batchYear: [],
      enrollmentStatus: [],
      enrollmentStartDate: '',
      enrollmentEndDate: ''
    }
    setTempFilters(clearedFilters)
    setDateRange({ from: undefined, to: undefined })
    onApplyFilters(clearedFilters)
    onClose()
  }

  const getActiveFilterCount = () => {
    return tempFilters.collegeId.length + 
           tempFilters.courseGroupId.length + 
           tempFilters.batchYear.length + 
           tempFilters.enrollmentStatus.length + 
           (tempFilters.enrollmentStartDate ? 1 : 0) + 
           (tempFilters.enrollmentEndDate ? 1 : 0)
  }

  // Check if colleges are available (indicates SYSTEM_ADMIN user)
  const isSystemAdmin = filterInfo.colleges && filterInfo.colleges.length > 0

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ListFilter className="h-5 w-5 text-gray-600" />
            Filters
            {getActiveFilterCount() > 0 && (
              <Badge className="h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
                {getActiveFilterCount()}
              </Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="grid flex-1 auto-rows-min gap-6 px-4">
          {/* Enrollment Status Filter */}
          <div className="grid gap-3">
            <Label className="text-sm font-medium text-gray-900">Status</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between font-normal"
                >
                  <span className="truncate">
                    {tempFilters.enrollmentStatus.length === 0
                      ? "Select status..."
                      : tempFilters.enrollmentStatus.length === 1
                      ? enrollmentStatusOptions.find((option) => option.value === tempFilters.enrollmentStatus[0])
                          ?.label
                      : `${tempFilters.enrollmentStatus.length} statuses selected`}
                  </span>
                  <div className="flex items-center gap-x-2 text-gray-900">
                    {tempFilters.enrollmentStatus.length > 0 && (
                      <Badge className="ml-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
                        {tempFilters.enrollmentStatus.length}
                      </Badge>
                    )}
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[22rem] p-3" align="start">
                <div className="space-y-3">
                  <div className="text-xs text-muted-foreground">
                    Select enrollment status
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {enrollmentStatusOptions.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-3"
                      >
                        <Checkbox
                          id={`status-${option.value}`}
                          checked={tempFilters.enrollmentStatus.includes(option.value)}
                          onCheckedChange={(checked) =>
                            handleEnrollmentStatusChange(option.value, !!checked)
                          }
                        />
                        <Label
                          htmlFor={`status-${option.value}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* College Filter - Only show for SYSTEM_ADMIN */}
          {isSystemAdmin && (
            <div className="grid gap-3">
              <Label className="text-sm font-medium text-gray-900">College</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between font-normal"
                    disabled={isLoadingInfo}
                  >
                    <span className="truncate">
                      {isLoadingInfo
                        ? "Loading colleges..."
                        : tempFilters.collegeId.length === 0
                        ? "Select colleges..."
                        : tempFilters.collegeId.length === 1
                        ? filterInfo.colleges?.find((c) => c.id === tempFilters.collegeId[0])
                            ?.name
                        : `${tempFilters.collegeId.length} colleges selected`}
                    </span>
                    <div className="flex items-center gap-x-2 text-gray-900">
                      {tempFilters.collegeId.length > 0 && (
                        <Badge className="ml-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
                          {tempFilters.collegeId.length}
                        </Badge>
                      )}
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[22rem] p-3" align="start">
                  <div className="space-y-3">
                    <div className="text-xs text-muted-foreground">
                      Select colleges
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {isLoadingInfo ? (
                        <div className="text-sm text-gray-500 text-center py-4">
                          Loading colleges...
                        </div>
                      ) : !filterInfo.colleges || filterInfo.colleges.length === 0 ? (
                        <div className="text-sm text-gray-500 text-center py-4">
                          No colleges available
                        </div>
                      ) : (
                        filterInfo.colleges.map((college) => (
                          <div
                            key={college.id}
                            className="flex items-center space-x-3"
                          >
                            <Checkbox
                              id={`college-${college.id}`}
                              checked={tempFilters.collegeId.includes(college.id)}
                              onCheckedChange={(checked) =>
                                handleCollegeChange(college.id, !!checked)
                              }
                            />
                            <Label
                              htmlFor={`college-${college.id}`}
                              className="text-sm font-normal cursor-pointer flex-1"
                            >
                              <div>{college.name}</div>
                              <div className="text-xs text-gray-500">
                                {college.code}
                              </div>
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Course Group Filter */}
          <div className="grid gap-3">
            <Label className="text-sm font-medium text-gray-900">Course Group</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between font-normal"
                  disabled={isLoadingInfo}
                >
                  <span className="truncate">
                    {isLoadingInfo
                      ? "Loading course groups..."
                      : tempFilters.courseGroupId.length === 0
                      ? "Select course groups..."
                      : tempFilters.courseGroupId.length === 1
                      ? filterInfo.courseGroups?.find((c) => c.id === tempFilters.courseGroupId[0])
                          ?.name
                      : `${tempFilters.courseGroupId.length} course groups selected`}
                  </span>
                  <div className="flex items-center gap-x-2 text-gray-900">
                    {tempFilters.courseGroupId.length > 0 && (
                      <Badge className="ml-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
                        {tempFilters.courseGroupId.length}
                      </Badge>
                    )}
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[22rem] p-3" align="start">
                <div className="space-y-3">
                  <div className="text-xs text-muted-foreground">
                    Select course groups
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {isLoadingInfo ? (
                      <div className="text-sm text-gray-500 text-center py-4">
                        Loading course groups...
                      </div>
                    ) : filterInfo.courseGroups.length === 0 ? (
                      <div className="text-sm text-gray-500 text-center py-4">
                        No course groups available
                      </div>
                    ) : (
                      filterInfo.courseGroups.map((courseGroup) => (
                        <div
                          key={courseGroup.id}
                          className="flex items-center space-x-3"
                        >
                          <Checkbox
                            id={`courseGroup-${courseGroup.id}`}
                            checked={tempFilters.courseGroupId.includes(courseGroup.id)}
                            onCheckedChange={(checked) =>
                              handleCourseGroupChange(courseGroup.id, !!checked)
                            }
                          />
                          <Label
                            htmlFor={`courseGroup-${courseGroup.id}`}
                            className="text-sm font-normal cursor-pointer flex-1"
                          >
                            <div>{courseGroup.name}</div>
                            <div className="text-xs text-gray-500">
                              {courseGroup.code}
                            </div>
                          </Label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Batch Year Filter */}
          <div className="grid gap-3">
            <Label className="text-sm font-medium text-gray-900">Batch Year</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between font-normal"
                  disabled={isLoadingInfo}
                >
                  <span className="truncate">
                    {isLoadingInfo
                      ? "Loading batch years..."
                      : tempFilters.batchYear.length === 0
                      ? "Select batch years..."
                      : tempFilters.batchYear.length === 1
                      ? tempFilters.batchYear[0]
                      : `${tempFilters.batchYear.length} batch years selected`}
                  </span>
                  <div className="flex items-center gap-x-2 text-gray-900">
                    {tempFilters.batchYear.length > 0 && (
                      <Badge className="ml-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
                        {tempFilters.batchYear.length}
                      </Badge>
                    )}
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[22rem] p-3" align="start">
                <div className="space-y-3">
                  <div className="text-xs text-muted-foreground">
                    Select batch years
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {isLoadingInfo ? (
                      <div className="text-sm text-gray-500 text-center py-4">
                        Loading batch years...
                      </div>
                    ) : filterInfo.batchYears.length === 0 ? (
                      <div className="text-sm text-gray-500 text-center py-4">
                        No batch years available
                      </div>
                    ) : (
                      filterInfo.batchYears.map((batchYear) => (
                        <div
                          key={batchYear}
                          className="flex items-center space-x-3"
                        >
                          <Checkbox
                            id={`batchYear-${batchYear}`}
                            checked={tempFilters.batchYear.includes(batchYear)}
                            onCheckedChange={(checked) =>
                              handleBatchYearChange(batchYear, !!checked)
                            }
                          />
                          <Label
                            htmlFor={`batchYear-${batchYear}`}
                            className="text-sm font-normal cursor-pointer flex-1"
                          >
                            {batchYear}
                          </Label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Date Filters */}
          <div className="grid gap-3">
            <Label className="text-sm font-medium text-gray-900">Enrollment Date Range</Label>
            <DateRangePicker
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              placeholder="Select enrollment date range"
              className="w-full !text-accent-foreground"
            />
          </div>
        </div>

        <SheetFooter>
          <Button onClick={handleApply} className="w-full">
            <Check className="h-4 w-4" />
            Apply Filters
          </Button>
          <Button
            variant="outline"
            onClick={handleClearAll}
            className="w-full"
            disabled={getActiveFilterCount() === 0}
          >
            Clear All Filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

export default StudentFilterDrawer;

